import * as React from "react";
import { cn } from "@/lib/utils";
import { useHoloTiming } from "./holoTiming";

export interface HoloBorderProps {
  /** Adds the soft glow (selected or in flight). The border itself always turns. */
  active?: boolean;
  /** When not active, shows the glow only while hovered (entry points that invite a click). */
  hoverActive?: boolean;
  radius?: "lg" | "xl" | "2xl";
  thickness?: 1 | 2;
  className?: string;
  children?: React.ReactNode;
}

const RADIUS_CLASS: Record<NonNullable<HoloBorderProps["radius"]>, string> = {
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
};

const THICKNESS_CLASS: Record<NonNullable<HoloBorderProps["thickness"]>, string> = {
  1: "p-px",
  2: "p-[2px]",
};

export const HoloBorder: React.FC<HoloBorderProps> = ({
  active = true,
  hoverActive = false,
  radius = "2xl",
  thickness = 2,
  className,
  children,
}) => {
  const timing = useHoloTiming();
  const glows = active || hoverActive;
  const motion = "animate-holo-rotate motion-reduce:animate-none";

  return (
    <div
      data-slot="holo-border"
      data-active={active ? "true" : "false"}
      style={timing}
      className={cn(
        "relative isolate bg-holo-conic",
        hoverActive && "group/holo",
        RADIUS_CLASS[radius],
        THICKNESS_CLASS[thickness],
        motion,
        className
      )}
    >
      {glows && (
        // The light lives in a blurred copy of the gradient behind the card; the border stays crisp.
        <span
          aria-hidden="true"
          data-slot="holo-glow"
          className={cn(
            "pointer-events-none absolute -inset-0.5 -z-10 rounded-[inherit] bg-holo-conic blur-sm transition-opacity duration-500",
            active ? "opacity-25" : "opacity-0 group-hover/holo:opacity-25",
            motion
          )}
        />
      )}
      <div className="rounded-[inherit] bg-white h-full">{children}</div>
    </div>
  );
};
